import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Snackbar, Alert, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TopMenuBar from './components/TopMenuBar';
import HomePage from './pages/HomePage';
import ManualBookingPage from './pages/ManualBookingPage';
import StaffAvailabilityPage from './pages/StaffAvailabilityPage';
import CommunicationsPage from './pages/CommunicationsPage';
import SettingsPage from './pages/SettingsPage';
import MediaPermissionsHelper from './components/MediaPermissionsHelper';
import { checkMediaPermissions } from './utils/mediaPermissions';
import darkTheme from './theme';
import './App.css';

function App() {
  const [activePage, setActivePage] = useState('home');
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'connected', 'disconnected', 'connecting'
  const [permissionError, setPermissionError] = useState(null);
  const [permissionSnackbarOpen, setPermissionSnackbarOpen] = useState(false);

  // Check for media device permissions on app startup
  useEffect(() => {
    checkAppMediaPermissions();
  }, []);

  // Function to check media device permissions
  const checkAppMediaPermissions = async () => {
    const result = await checkMediaPermissions();

    if (!result.granted && result.errorMessage) {
      setPermissionError(result.errorMessage);
      setPermissionSnackbarOpen(true);
    } else {
      setPermissionError(null);
      setPermissionSnackbarOpen(false);
    }
  };

  const handlePermissionSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setPermissionSnackbarOpen(false);
  };

  const handlePageChange = (pageId) => {
    setActivePage(pageId);
  };

  const handleConnectionToggle = () => {
    if (connectionStatus === 'disconnected') {
      setConnectionStatus('connecting');
      // Simulate connection process
      setTimeout(() => {
        setConnectionStatus('connected');
      }, 2000);
    } else if (connectionStatus === 'connected') {
      setConnectionStatus('disconnected');
    }
  };

  const renderPage = () => {
    switch (activePage) {
      case 'home':
        return <HomePage />;
      case 'manual-booking':
        return <ManualBookingPage />;
      case 'staff-availability':
        return <StaffAvailabilityPage />;
      case 'communications':
        return <CommunicationsPage />;
      case 'settings':
        return <SettingsPage />;
      default:
        return <HomePage />;
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{
        width: '100vw',
        minHeight: '100vh',
        bgcolor: '#121212',
        overflow: 'visible'
      }}>
        <TopMenuBar
          activePage={activePage}
          onPageChange={handlePageChange}
          connectionStatus={connectionStatus}
          onConnectionToggle={handleConnectionToggle}
        />

        <Box sx={{
          width: '100%',
          minHeight: '100vh',
          overflow: 'visible'
        }}>
          {/* Global Permission Helper (minimal variant) */}
          <Box sx={{ maxWidth: '1200px', mx: 'auto', px: 2, pt: 2 }}>
            <MediaPermissionsHelper
              variant="minimal"
              onPermissionChange={(result) => {
                if (!result.granted && result.errorMessage) {
                  setPermissionError(result.errorMessage);
                }
              }}
            />
          </Box>
          {renderPage()}
        </Box>

        {/* Permission Error Snackbar */}
        <Snackbar
          open={permissionSnackbarOpen}
          autoHideDuration={15000}
          onClose={handlePermissionSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handlePermissionSnackbarClose}
            severity="warning"
            sx={{ width: '100%' }}
            action={
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={checkAppMediaPermissions}
                >
                  Try Again
                </Button>
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={handlePermissionSnackbarClose}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            {permissionError}
            <Box sx={{ mt: 1 }}>
              <strong>How to fix:</strong> Click the camera icon in your browser's address bar and select "Allow" for both camera and microphone.
            </Box>
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
export default App;